import React, { HTMLAttributes } from 'react';

export type TextVariant =
  | 'title1'
  | 'title2'
  | 'title3'
  | 'page subtitle'
  | 'bread-semibold'
  | 'bread-medium'
  | 'bread-normal'
  | 'notice'
  | 'form-caption'
  | 'table'
  | 'appbar'
  | 'appbar-large';

export type TextAlign = 'left' | 'center' | 'right';
export type TextColor =
  | 'hint'
  | 'slight'
  | 'light'
  | 'medium'
  | 'dark'
  | 'green'
  | 'error'
  | 'warning'
  | 'info'
  | 'white';

export interface VoxelTextProps extends Omit<HTMLAttributes<HTMLParagraphElement>, 'color'> {
  variant?: TextVariant;
  paddingBot?: boolean;
  paddingTop?: boolean;
  children: React.ReactNode;
  className?: string;
  align?: TextAlign;
  colorClassName?: string;
  color?: TextColor;
  div?: boolean;
  span?: boolean;
  disabled?: boolean;
  noWrap?: boolean;
  ellipsis?: boolean;
  uppercase?: boolean;
  italic?: boolean;
  userSelectClass?: string;
}

export const VoxelText = React.forwardRef<HTMLParagraphElement, VoxelTextProps>(
  (
    {
      variant = 'bread-normal',
      align = 'left',
      children,
      userSelectClass = undefined,
      paddingTop = false,
      paddingBot = false,
      colorClassName = undefined,
      className = '',
      color,
      div = false,
      span = false,
      disabled = false,
      noWrap = false,
      ellipsis = false,
      uppercase = false,
      italic = false,
      ...defaultProps
    },
    ref
  ) => {
    let combinedClassNames = className + ' ';

    // Color
    let dark_color = ' text-voxel-gray-900 ';
    let mid_color = ' text-voxel-gray-700 ';
    let light_color = ' text-voxel-gray-600 ';
    let slight_color = ' text-voxel-gray-500 ';
    let hint_color = ' text-voxel-gray-400 ';

    if (color === 'dark') {
      dark_color = dark_color;
      mid_color = dark_color;
      light_color = dark_color;
      hint_color = dark_color;
    }
    if (color === 'medium') {
      dark_color = mid_color;
      mid_color = mid_color;
      light_color = mid_color;
      hint_color = mid_color;
    }
    if (color === 'light') {
      dark_color = light_color;
      mid_color = light_color;
      light_color = light_color;
      hint_color = light_color;
    }

    if (color === 'white') {
      dark_color = 'text-white';
      mid_color = 'text-white';
      light_color = 'text-white';
      hint_color = 'text-white';
    }
    if (color === 'slight') {
      dark_color = slight_color;
      mid_color = slight_color;
      light_color = slight_color;
      hint_color = slight_color;
    }
    if (color === 'hint') {
      dark_color = hint_color;
      mid_color = hint_color;
      light_color = hint_color;
      hint_color = hint_color;
    }
    if (color === 'warning') {
      dark_color = ' text-voxel-orange-700 ';
      mid_color = ' text-voxel-orange-700 ';
      light_color = ' text-voxel-orange-700 ';
      hint_color = ' text-voxel-orange-700 ';
    }
    if (color === 'error') {
      dark_color = ' text-error-500 ';
      mid_color = ' text-error-500 ';
      light_color = ' text-error-500 ';
      hint_color = ' text-error-500 ';
    }

    if (colorClassName) {
      dark_color = ' ' + colorClassName;
      mid_color = ' ' + colorClassName;
      light_color = ' ' + colorClassName;
      slight_color = ' ' + colorClassName;
      hint_color = ' ' + colorClassName;
    }

    // Select
    else if (!userSelectClass && variant?.includes('title')) userSelectClass = ' select-text ';
    combinedClassNames += ' ' + userSelectClass + ' ';

    // Variants
    if (variant === 'title1') {
      combinedClassNames += ' text-2xl font-sans font-semibold ';
      combinedClassNames += dark_color;
      combinedClassNames += paddingTop ? ' mt-1 ' : '';
      combinedClassNames += paddingBot ? ' mb-2 ' : '';
    }
    if (variant === 'title2') {
      combinedClassNames += ' text-lg font-sans font-semibold ';
      combinedClassNames += dark_color;
      combinedClassNames += paddingTop ? ' mt-2 ' : '';
      combinedClassNames += paddingBot ? ' mb-2 ' : '';
    }
    if (variant === 'title3') {
      combinedClassNames += ' text-base font-sans font-semibold ';
      combinedClassNames += dark_color;
      combinedClassNames += paddingTop ? ' mt-4 ' : '';
      combinedClassNames += paddingBot ? ' mb-4 ' : '';
    }
    if (variant === 'bread-semibold') {
      combinedClassNames += ' font-sans font-semibold ';
      combinedClassNames += disabled ? hint_color : '';
    }
    if (variant === 'bread-medium') {
      combinedClassNames += ' font-sans font-medium ';
      combinedClassNames += paddingTop ? ' mt-4 ' : '';
      combinedClassNames += paddingBot ? ' mb-4 ' : '';
      combinedClassNames += disabled ? hint_color : '';
    }
    if (variant === 'bread-normal') {
      combinedClassNames += ' text-sm font-normal leading-6 ';
      combinedClassNames += dark_color;
      combinedClassNames += paddingTop ? ' mt-2 ' : '';
      combinedClassNames += paddingBot ? ' mb-2 ' : '';
    }

    if (variant === 'notice') {
      combinedClassNames += ' text-2xl font-sans font-medium leading-relaxed ';
      combinedClassNames += mid_color;
      combinedClassNames += paddingTop ? ' mt-4 ' : '';
      combinedClassNames += paddingBot ? ' mb-4 ' : '';
    }
    if (variant === 'page subtitle') {
      combinedClassNames += ' text-lg font-sans font-medium leading-7';
      combinedClassNames += mid_color;
      combinedClassNames += paddingTop ? ' mt-1 ' : '';
      combinedClassNames += paddingBot ? ' mb-1 ' : '';
    }


    if (variant === 'form-caption') {
      combinedClassNames += ' text-xs font-normal font-sans ';
      combinedClassNames += light_color;
      combinedClassNames += paddingTop ? ' mt-1 ' : '';
      combinedClassNames += paddingBot ? ' mb-1 ' : '';
    }

    if (variant === 'table') {
      combinedClassNames += ' text-[13px] font-sans whitespace-nowrap font-normal ';
      combinedClassNames += dark_color;
    }

    if (variant === 'appbar') {
      combinedClassNames += ' font-sans ';
      combinedClassNames += dark_color;
      combinedClassNames += ' capitalize text-sm font-medium ';
    }

    if (variant === 'appbar-large') {
      combinedClassNames += ' font-sans ';
      combinedClassNames += dark_color;
      combinedClassNames += ' capitalize text-base font-medium ';
    }



    // Uppercase
    combinedClassNames += `${uppercase ? ' uppercase tracking-wider ' : ''}`;

    // Aligning
    combinedClassNames += `${align === 'left' ? ' text-left ' : ''} `;
    combinedClassNames += `${align === 'center' ? ' text-center ' : ''}`;
    combinedClassNames += `${align === 'right' ? ' text-right ' : ''}`;

    // No wrap
    combinedClassNames += ` ${noWrap ? ' whitespace-nowrap ' : ''} `;

    //Italic
    combinedClassNames += ` ${italic ? ' italic ' : ''} `;

    // Ellipsis
    combinedClassNames += ` ${ellipsis ? ' truncate ' : ''} `;

    // Disabled
    combinedClassNames += ` ${disabled ? hint_color : ''} `;

    if (span) {
      return (
        <span className={combinedClassNames} {...defaultProps} ref={ref}>
          {children}
        </span>
      );
    }

    if (div) {
      return (
        <div className={combinedClassNames} {...defaultProps} ref={ref}>
          {children}
        </div>
      );
    }

    return (
      <p className={combinedClassNames} {...defaultProps} ref={ref}>
        {children}
      </p>
    );
  }
);

export default VoxelText;
